import axios from "axios";

// Detectar si estamos en local (npm start) o producción (build)
const defaultUrl =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:8888/agenda_alavuelta360/public/api" // Local
    : "/api"; // Producción Hostinger (RAÍZ, no /agenda/api)

const API_BASE_URL = process.env.REACT_APP_API_URL || defaultUrl;

// Log en desarrollo para debugging
if (process.env.NODE_ENV === "development") {
  console.log("[API Config] Base URL:", API_BASE_URL);
  console.log("[API Config] Hostname:", window.location.hostname);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 segundos timeout
});

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log solo en desarrollo
    if (process.env.NODE_ENV === "development") {
      const fullUrl = `${config.baseURL}${config.url}`;
      console.log("\n========== API REQUEST ==========");
      console.log(`[API Request] ${config.method?.toUpperCase()} ${fullUrl}`);
      console.log(`[API Request] Endpoint: ${config.url}`);
      console.log("================================\n");
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    // Si la respuesta es 200 pero devuelve HTML (detección de redirección silenciosa)
    if (
      response.status === 200 &&
      typeof response.data === "string" &&
      response.data.includes("<!doctype")
    ) {
      if (process.env.NODE_ENV === "development") {
        console.error(
          "[API] Redirección detectada - URL:",
          response.config.url,
        );
      }
      return Promise.reject(
        new Error(
          "Redirección detectada: El servidor devolvió HTML. Verifica la ruta de la API.",
        ),
      );
    }

    // ============================================================================
    // ESTANDARIZAR RESPUESTA - ASEGURAR QUE response.data SIEMPRE SEA EL DATO ÚTIL
    // ============================================================================
    // Si la respuesta tiene la estructura { success, message, data: {...} }
    // Extraer el contenido de `data` y ponerlo en response.data para consistencia
    if (response.data && typeof response.data === "object") {
      // Si tiene la estructura de API response con { success, message, data }
      if ("success" in response.data && "data" in response.data) {
        // Reemplazar response.data con solo los datos útiles
        const originalData = response.data;
        response.data = response.data.data;

        if (process.env.NODE_ENV === "development") {
          console.log(`[API Response] Normalized ${response.config.url}:`, {
            original: originalData,
            extracted: response.data,
          });
        }
      }
    }

    return response;
  },
  (error) => {
    // ============================================================================
    // MANEJAR 401 - SESIÓN INVÁLIDA O EXPIRADA
    // ============================================================================
    if (error.response?.status === 401) {
      console.warn("\n🔴 [AUTH] Sesión inválida. Limpiando y redirigiendo...");

      // Prevenir múltiples redirecciones
      if (!sessionStorage.getItem("redirecting_to_login")) {
        sessionStorage.setItem("redirecting_to_login", "true");
        // Limpiar todos los datos de sesión (sessionStorage y legacy localStorage)
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("session_id");
        sessionStorage.removeItem("device_info");
        sessionStorage.removeItem("previous_session_closed");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("session_id");
        localStorage.removeItem("device_info");
        localStorage.removeItem("previous_session_closed");

        // Redirigir después de un pequeño delay para permitir que se procese
        setTimeout(() => {
          window.location.href = "/login";
        }, 100);
      }

      // No propagar el error más allá - evita loops infinitos
      const authError = new Error("Sesión expirada - Redirigiendo a login");
      authError.isAuthError = true;
      authError.response = { status: 401 };
      return Promise.reject(authError);
    }

    // ============================================================================
    // OTROS ERRORES - MOSTRAR EN CONSOLA
    // ============================================================================
    console.error("\n========== API ERROR ==========");
    if (!error.response) {
      console.error("[API Error] ❌ Error de conexión");
      console.error("[API Error] No se pudo conectar al servidor");
      console.error(
        "[API Error] URL:",
        error.config?.baseURL + error.config?.url,
      );
      console.error("[API Error] Mensaje:", error.message);
    } else {
      console.error(
        `[API Error] HTTP ${error.response.status} - ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      );
      console.error(
        "[API Error] URL completa:",
        error.config?.baseURL + error.config?.url,
      );
      console.error("[API Error] Respuesta:", error.response.data);
      console.error("[API Error] Headers:", error.response.headers);
    }
    console.error("================================\n");

    return Promise.reject(error);
  },
);

export default api;
